/** @format */

import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import { Alert, AlertTitle, Slide, Snackbar, Stack } from "@mui/material";
import type { AlertColor } from "@mui/material";

/**
 * Lightweight app-wide toast notification system.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success("Saved");
 *   toast.error("Sync failed", { title: "QuickBooks error", durationMs: 8000 });
 *
 * Multiple toasts stack vertically in the bottom-right. Newest on top.
 * Auto-dismiss after `durationMs` (default 4500ms). Errors default 7000ms.
 */

export type ToastSeverity = AlertColor; // "success" | "info" | "warning" | "error"

export interface ToastOptions {
    title?: string;
    durationMs?: number;
}

export interface ToastEntry {
    id: number;
    severity: ToastSeverity;
    message: React.ReactNode;
    title?: string;
    durationMs: number;
}

interface ToastContextValue {
    show: (
        severity: ToastSeverity,
        message: React.ReactNode,
        opts?: ToastOptions,
    ) => void;
    success: (message: React.ReactNode, opts?: ToastOptions) => void;
    info: (message: React.ReactNode, opts?: ToastOptions) => void;
    warning: (message: React.ReactNode, opts?: ToastOptions) => void;
    error: (message: React.ReactNode, opts?: ToastOptions) => void;
    dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION: Record<ToastSeverity, number> = {
    success: 4500,
    info: 4500,
    warning: 6000,
    error: 7000,
};

const MAX_VISIBLE = 4;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [toasts, setToasts] = useState<ToastEntry[]>([]);
    const idRef = useRef(0);

    const dismiss = useCallback((id: number) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const show = useCallback(
        (
            severity: ToastSeverity,
            message: React.ReactNode,
            opts?: ToastOptions,
        ) => {
            idRef.current += 1;
            const id = idRef.current;
            const durationMs =
                opts?.durationMs ?? DEFAULT_DURATION[severity] ?? 4500;
            setToasts((prev) => {
                const next = [
                    { id, severity, message, title: opts?.title, durationMs },
                    ...prev,
                ];
                // Cap visible toasts — drop oldest when over the limit.
                return next.slice(0, MAX_VISIBLE);
            });
        },
        [],
    );

    const value = useMemo<ToastContextValue>(
        () => ({
            show,
            success: (m, o) => show("success", m, o),
            info: (m, o) => show("info", m, o),
            warning: (m, o) => show("warning", m, o),
            error: (m, o) => show("error", m, o),
            dismiss,
        }),
        [show, dismiss],
    );

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastStack toasts={toasts} onDismiss={dismiss} />
        </ToastContext.Provider>
    );
};

interface ToastStackProps {
    toasts: ToastEntry[];
    onDismiss: (id: number) => void;
}

const ToastStack: React.FC<ToastStackProps> = ({ toasts, onDismiss }) => {
    return (
        <Snackbar
            open={toasts.length > 0}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            // Snackbar itself doesn't auto-hide; individual Alerts manage timers
            // via a per-entry setTimeout below. We just use Snackbar for the
            // positioning + zIndex.
            sx={{
                bottom: { xs: 16, md: 24 },
                right: { xs: 12, md: 24 },
                left: "auto",
                width: { xs: "calc(100% - 24px)", sm: "auto" },
                maxWidth: { xs: "calc(100% - 24px)", sm: 420 },
                zIndex: (theme) => theme.zIndex.snackbar + 10,
            }}
            TransitionComponent={Slide}
        >
            <Stack spacing={1.2} sx={{ width: "100%" }}>
                {toasts.map((t) => (
                    <ToastItem key={t.id} entry={t} onDismiss={onDismiss} />
                ))}
            </Stack>
        </Snackbar>
    );
};

const ToastItem: React.FC<{
    entry: ToastEntry;
    onDismiss: (id: number) => void;
}> = ({ entry, onDismiss }) => {
    // Per-entry auto-dismiss timer (Snackbar's autoHideDuration only handles a
    // single message, so we manage it ourselves to support stacking).
    React.useEffect(() => {
        if (entry.durationMs <= 0) return;
        const timer = setTimeout(() => onDismiss(entry.id), entry.durationMs);
        return () => clearTimeout(timer);
    }, [entry.id, entry.durationMs, onDismiss]);

    return (
        <Alert
            severity={entry.severity}
            variant="filled"
            elevation={6}
            onClose={() => onDismiss(entry.id)}
            sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "0.88rem",
                fontWeight: 500,
                alignItems: "flex-start",
                borderRadius: "10px",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.18)",
                "& .MuiAlert-message": { paddingY: 0.5 },
            }}
        >
            {entry.title && (
                <AlertTitle
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 700,
                        fontSize: "0.92rem",
                        marginBottom: 0.4,
                    }}
                >
                    {entry.title}
                </AlertTitle>
            )}
            {entry.message}
        </Alert>
    );
};

/**
 * Hook for showing toasts from any component below <ToastProvider>.
 * Throws if used outside the provider.
 */
export function useToast(): ToastContextValue {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error("useToast must be used inside <ToastProvider>");
    }
    return ctx;
}

/**
 * Imperative escape hatch — lets non-component code (axios interceptors,
 * Redux middleware) fire toasts without needing the hook. Lazily set by
 * <ToastProvider> on mount.
 */
let imperativeToast: ToastContextValue | null = null;

export const ToastBridge: React.FC = () => {
    const toast = useToast();
    React.useEffect(() => {
        imperativeToast = toast;
        return () => {
            imperativeToast = null;
        };
    }, [toast]);
    return null;
};

export function notifyToast(
    severity: ToastSeverity,
    message: React.ReactNode,
    opts?: ToastOptions,
): void {
    if (imperativeToast) {
        imperativeToast.show(severity, message, opts);
    } else if (typeof console !== "undefined") {
        // eslint-disable-next-line no-console
        console.warn(
            "[notifyToast] called before ToastProvider mounted:",
            severity,
            message,
        );
    }
}
